import { Autocomplete, TextField, Box, Typography } from "@mui/material";

import { formatSongTitle } from "./formatSongTitle";


interface Performance {
  entry_number?: number;
}

interface Song {
  song_id: number;
  program: string;
  song_title?: string | null;
  performance?: Performance | null;
}

interface SongSelectProps {
  songs: Song[];
  songId: number | null;
  eventId?: string;
  setSongId: (id: number | null) => void;
  setNewPerformanceData: React.Dispatch<React.SetStateAction<any>>;
}

const SongSelect = ({
  songs,
  songId,
  eventId,
  setSongId,
  setNewPerformanceData
}: SongSelectProps) => {
  const options = songs
    .filter((s) => !s.performance?.entry_number)
    .map((song) => ({
      song_id: song.song_id,
      program: song.program,
      song_title: song.song_title,
      performance: song.performance
    }))
    .sort((a, b) => a.program.localeCompare(b.program));

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={songId !== null ? options.find((o) => o.song_id === songId) || { song_id: songId, program: "", song_title: "", performance: null } : null}
      groupBy={(option) => option.program}

      filterOptions={(opts, state) => {
        const term = state.inputValue.toLowerCase();
        return opts.filter((o) =>
          `${o.song_id} ${o.song_title ?? ""} ${o.program}`
            .toLowerCase()
            .includes(term)
        );
      }}

      getOptionLabel={(option) =>
        typeof option === "string" ? option : `${option.song_id}${option.song_title ? ` - ${formatSongTitle(option.song_title, option.performance?.entry_number ?? 0)}` : ""}`
      }

      onChange={(e, newValue) => {
        console.log(e);
        let id: number | null = null;

        if (typeof newValue === "string") {
          const parsed = parseInt(newValue, 10);
          id = isNaN(parsed) ? null : parsed;
        } else if (newValue) {
          id = newValue.song_id;
        }

        // If field cleared completely, allow null
        if (id === null && newValue === "") id = 0;

        setSongId(id);
        setNewPerformanceData((prev: any) => ({
          ...prev,
          song_id: id
        }));
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Enter SongID or 0 to detach"
          size="small"
          onChange={(e) => {
            const val = Number(e.target.value);
            if (!isNaN(val)) {
              setSongId(val);
              setNewPerformanceData((prev: any) => ({
                ...prev,
                song_id: val
              }));
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "36px",
              padding: "0 !important",
              fontSize: "13px"
            },
            "& .MuiAutocomplete-inputRoot": {
              padding: "0 !important"
            },
            "& .MuiAutocomplete-input": {
              padding: "4px 6px !important",
              fontSize: "13px"
            }
          }}
        />
      )}

      renderOption={(props, option) => {
        const missingSong = !option.song_title;
        return (
          <Box
            component="li"
            {...props}
            title={`${option.song_id}${option.song_title ? ` - ${formatSongTitle(option.song_title, option.performance?.entry_number ?? 0)}` : ""} (${option.program})`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              fontSize: "13px",
              px: 1,
              py: 0.3
            }}
          >
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
                flexGrow: 1
              }}
            >
              {option.song_id} {option.song_title ? `- ${formatSongTitle(option.song_title, option.performance?.entry_number ?? 0)}` : ""}
            </Typography>

            {missingSong && (
              <a
                href={`/music/${eventId ?? ""}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "12px",
                  color: "#4545ff",
                  textDecoration: "none",
                  marginLeft: "12px",
                  flexShrink: 0
                }}
              >
                Upload Song
              </a>
            )}
          </Box>
        );
      }}

      renderGroup={(params) => (
        <li key={params.key}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              px: 1,
              py: 0.4,
              bgcolor: "#f5f5f5"
            }}
          >
            {params.group}
          </Typography>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}

      ListboxProps={{
        sx: {
          fontSize: "13px",
          "& li": {
            minHeight: "26px"
          }
        }
      }}
    />
  );
};

export default SongSelect;